import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { ShortUrl } from '@air/api';
import { type BoardsListOptions, type SortDirection, type SortField } from '@air/api/types';
import { isAirrorInstance } from '@air/utils-error';

import {
  getPublicBoardPageKey,
  type PublicBoardPage,
} from '~/components/Layouts/PublicBoardLayout/hooks/usePublicBoardPage';
import { PageLimits } from '~/constants/PageLimits';

type ListCollectionScopedBoardsParams = {
  shortId: string;
  parentId: string;
  collectionId: string;
  params: Partial<BoardsListOptions>;
  sortFieldName: SortField['name'];
  sortDirection: SortDirection;
  cursor: string | null;
};

/**
 * Lists a drilled board's children scoped to the collection it is being viewed within. The legacy
 * `listChildrenBoardsByShortId` signature cannot carry a collection scope, so the backend resolves
 * the share link's ROOT collection — wrong for a board drilled into from a collection sub-page.
 * The collection-aware endpoint takes the scope explicitly; collection surfaces use it for every
 * drill (root included) rather than relying on that backend default.
 */
const listCollectionScopedBoards = async ({
  shortId,
  parentId,
  collectionId,
  params,
  sortFieldName,
  sortDirection,
  cursor,
}: ListCollectionScopedBoardsParams) => {
  // `view` exists to scope the legacy endpoint; this endpoint carries the drill as
  // `parentBoardId` and its request type has no `view`, so strip it rather than send a param the
  // endpoint does not know.
  const { view, ...boardsParams } = params;

  const result = await ShortUrl.Boards.list(shortId, {
    ...boardsParams,
    parentBoardId: parentId,
    collectionId,
    limit: !cursor ? PageLimits.subBoards.init : PageLimits.subBoards.more,
    cursor,
    sortField: {
      direction: sortDirection,
      name: sortFieldName,
    },
    numThumbnails: 1,
    includeAncestors: true,
  });

  if (isAirrorInstance(result)) {
    throw result;
  }

  return result;
};

export const usePublicBoardsFetcher = () => {
  const queryClient = useQueryClient();

  const publicBoardsFetcher = useCallback(
    async ({
      params,
      sortFieldName,
      sortDirection,
      parentId,
      shortId,
      cursor,
      collectionId,
    }: {
      shortId: string;
      parentId: string;
      params: Partial<BoardsListOptions>;
      sortFieldName: SortField['name'];
      sortDirection: SortDirection;
      cursor: string | null;
      /** See `listCollectionScopedBoards` — absent ONLY on the standalone board link. */
      collectionId?: string;
    }) => {
      const res = collectionId
        ? await listCollectionScopedBoards({
            shortId,
            parentId,
            collectionId,
            params,
            sortFieldName,
            sortDirection,
            cursor,
          })
        : await ShortUrl.listChildrenBoardsByShortId(shortId, parentId, {
            ...params,
            limit: !cursor ? PageLimits.subBoards.init : PageLimits.subBoards.more,
            cursor,
            sortField: {
              direction: sortDirection,
              name: sortFieldName,
            },
            numThumbnails: 1,
            includeAncestors: true,
          });

      res.data.forEach((board) => {
        const { ancestors, thumbnails } = board;

        // Both endpoints are asked for ancestors and a thumbnail, but only the legacy signature
        // encodes that in its types — narrow so a board the page cache's contract can't hold is
        // skipped instead of asserted.
        if (!ancestors || !thumbnails) {
          return;
        }

        const cachedBoard = queryClient.getQueryData<PublicBoardPage>(
          getPublicBoardPageKey({
            shortId,
            boardId: board.id,
          }),
        );

        if (!cachedBoard) {
          queryClient.setQueryData<PublicBoardPage>(
            getPublicBoardPageKey({
              shortId,
              boardId: board.id,
            }),
            { ...board, ancestors, thumbnails },
          );
        }
      });

      return res;
    },
    [queryClient],
  );

  return {
    publicBoardsFetcher,
  };
};
